import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { CommonMessagesService } from '../../services/common-messages.service';
import { UsersService } from '../../services/users.service';
import { User } from '../../model/user';
import { AuthGuard } from '../../common/auth-guard.service';
import { LoggedUser } from '../../common/logged-user.service';
import { Organization } from '../../model/organization';
import { Tenant } from '../../model/tenant';
import { AbstractControl, FormBuilder, FormGroup, ValidationErrors, ValidatorFn } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TenantsService } from '../../services/tenants.service';

@Component({
  selector: 'app-users',
  templateUrl: './users.component.html',
  styleUrls: ['./users.component.scss']
})
export class UsersComponent implements OnInit, OnDestroy {

  users: User[];
  totalUsers: number;
  loadingUsers: boolean;

  rows = 10;
  first = 0;
  selected = [];

  tenantOptions: Tenant[];
  organizationOptions: Organization[];
  userBelongsToSpecificOrganization = false;

  filtersVisible: boolean;
  filtersForm: FormGroup;
  private activeTenantSubscription: Subscription | undefined;

  filtersNotEmpty: ValidatorFn = (control: AbstractControl): ValidationErrors | null => {
    const username: string | null | undefined = control.get('username').value;
    const tenant: string | null | undefined = control.get('tenant').value;
    const organization: string | null | undefined = control.get('organization').value;

    return !username
        && (!tenant || this.tenantOptions.length === 1)
        && (!organization || this.userBelongsToSpecificOrganization)
      ? { allFiltersEmpty: true }
      : null;
  }

  constructor(
    private usersService: UsersService,
    private commonMessagesService: CommonMessagesService,
    private tenantsService: TenantsService,
    public authGuard: AuthGuard,
    private fb: FormBuilder,
    public loggedUser: LoggedUser
  ) { }

  ngOnInit(): void {
    this.users = new Array(this.rows);
    this.filtersForm = this.fb.group({
      username: [undefined],
      tenant: [undefined],
      organization: [undefined]
    }, { validators: [this.filtersNotEmpty] });
    this.setUserTenantAndOrganization();
    if ( this.tenantOptions.length > 1 ) {
      this.activeTenantSubscription = this.filtersForm.get('tenant').valueChanges.subscribe((tenantUuid: string) => {
        if ( !tenantUuid ) {
          this.organizationOptions = [];
          return;
        }
        this.tenantsService.getTenantOrganizations(tenantUuid).subscribe(({ organizations }) => {
          this.organizationOptions = [...organizations];
        });
      });
    }
  }

  private setUserTenantAndOrganization(): void {
    this.tenantOptions = this.loggedUser.userTenants;
    if ( this.tenantOptions.length === 1 ) {
      this.filtersForm.get('tenant').setValue(this.loggedUser.userTenants[0].uuid);
      this.organizationOptions = this.loggedUser.userOrganizations;
      if ( this.loggedUser.userOrganizations.length === 1 ) {
        this.filtersForm.get('organization').setValue(this.loggedUser.userOrganizations[0].uuid);
        this.userBelongsToSpecificOrganization = true;
      }
    }
  }

  private loadUsers(offset?: number, limit?: number): void {
    this.loadingUsers = true;
    this.users = new Array(this.rows);
    const username: string | null | undefined = this.filtersForm.get('username').value;
    this.usersService.getUsers(offset, limit, {
      ...this.filtersForm.getRawValue(),
      username: !username ? username : username.trim()
    }).subscribe(response => {
      this.users = response.users;
      this.totalUsers = response.totalElements;
      this.loadingUsers = false;
    });
  }

  onPageChange(event): void {
    this.loadUsers(event.first / event.rows, event.rows);
  }

  resetFilters(): void {
    this.filtersForm.reset({
      username: undefined,
      tenant: undefined,
      organization: undefined
    });
    this.setUserTenantAndOrganization();
    this.applyFilters();
  }

  applyFilters(): void {
    this.filtersVisible = false;
    this.first = 0;
    this.loadUsers(this.first / this.rows, this.rows);
  }

  confirmAndDelete(user: User): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => {
      this.deleteUser(user.uuid, user.tenantUUID);
    });
  }

  deleteUser(userUuid: string, tenantUuid: string): void {
    this.usersService.deleteUser(userUuid, tenantUuid).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadUsers(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status);
    });
  }

  confirmAndDeleteSelected(): void {
    this.commonMessagesService.showDeleteRequestConfirmation(() => this.deleteSelected(), true);
  }

  deleteSelected(): void {
    if ( this.selected.length === 0 ) { return; }
    const uuids = this.selected.map(item => ({ uuid: item.uuid, tenantUUID: item.tenantUUID })); // TODO add tenantUUID to the object

    this.usersService.deleteUsers(uuids).toPromise().then(() => {
      this.commonMessagesService.showSuccessfullyDeletedMessage();
      this.loadUsers(this.first / this.rows, this.rows);
    }, error => {
      this.commonMessagesService.showErrorWhileDeletingMessage(error.status, true);
    });
  }

  @HostListener('window:keyup', ['$event'])
  keyEvent(event: KeyboardEvent): void {
    if ( event.key === 'f' ) {
      this.filtersVisible = true;
    }
    if ( event.key === 'Escape' ) {
      this.filtersVisible = false;
    }
  }

  ngOnDestroy(): void {
    this.activeTenantSubscription?.unsubscribe();
  }

}
